/**
 * The copied group's containers, folded (27/10/2026: "9 of 9 · 45 drops"); open, one row each:
 * bring it or not, its chip (the board's), the drop slots it brings, and what it loses or needs a
 * look — small muted lines under it. A route test wears its radio mast.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Boxes } from 'lucide-react';
import type { GroupCopyContainerRow } from '@trafficpulse/shared';

import { Checkbox } from '@/components/ui';
import { ContainerChip } from '@/features/drop-generator/components/shared/ContainerChip';
import { cn } from '@/lib/utils';

import { ReviewFold } from './ReviewFold';

interface Props {
  containers: GroupCopyContainerRow[];
  onTake: (sourceId: string, take: boolean) => void;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const ContainerLine: FC<{ row: GroupCopyContainerRow; onTake: Props['onTake'] }> = ({ row, onTake }) => {
  const routeTest = row.kind === 'ROUTE_TEST';
  return (
    <div className={cn('flex min-w-0 flex-col gap-0.5 px-5 py-2 transition-opacity', !row.take && 'opacity-60')}>
      <label className="flex min-w-0 cursor-pointer items-center gap-2.5">
        <Checkbox checked={row.take} onCheckedChange={(v) => onTake(row.sourceId, v === true)} aria-label={`Bring ${row.name}`} />
        <ContainerChip name={row.name} color={row.color} routeTest={routeTest} className="min-w-0 max-w-[60%]" />
        {routeTest ? <span className="shrink-0 text-[11px] text-muted-foreground">route test</span> : null}
        <span className="ml-auto shrink-0 font-mono text-xs tabular-nums text-muted-foreground">{plural(row.slots, 'slot')}</span>
      </label>
      {row.issues.map((issue) => (
        <span key={issue} className="pl-[26px] text-[11px] leading-snug text-muted-foreground">{issue}</span>
      ))}
    </div>
  );
};

export const ContainerRows: FC<Props> = ({ containers, onTake }) => {
  const taken = containers.filter((c) => c.take);
  const drops = taken.reduce((n, c) => n + c.slots, 0);
  const summary = containers.length === 0 ? 'none — only the master config comes' : `${taken.length} of ${containers.length} come · ${plural(drops, 'drop')}, exactly as they are`;
  return (
    <ReviewFold icon={Boxes} title="Containers" count={containers.length} summary={summary}>
      <p className="px-5 py-2 text-[11.5px] text-muted-foreground">They arrive switched off and the group&apos;s auto-send arrives off — switch each one on after a look.</p>
      {containers.map((c) => <ContainerLine key={c.sourceId} row={c} onTake={onTake} />)}
    </ReviewFold>
  );
};
